import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Me } from '../api/types';
import { useMembership, useReadOnly, useUpdateLanguage } from '../auth/session';
import { applyLanguage, dateLocale, LANGUAGES } from '../i18n';
import { formatDate } from '../lib/format';
import { APP_VERSION } from '../lib/version';
import { Dialog } from './Dialog';
import { PrivacyNotice } from './PrivacyNotice';
import ui from '../styles/ui.module.css';
import styles from './ProfileDialog.module.css';

interface Props {
  me: Me;
  open: boolean;
  onClose: () => void;
}

/**
 * The signed-in user's profile: who they are in the choir, the interface language, the privacy
 * notice they read when they joined, and the app's version.
 * The language changes at once and is saved to the account, so it follows the user to every
 * computer and session.
 */
export function ProfileDialog({ me, open, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const updateLanguage = useUpdateLanguage();
  const membership = useMembership();
  const readOnly = useReadOnly();
  const [privacyOpen, setPrivacyOpen] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  function choose(language: string) {
    if (language === i18n.language) return;
    const previous = i18n.language;
    applyLanguage(language);
    updateLanguage.mutate(language, { onError: () => applyLanguage(previous) });
  }

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-labelledby="profile-title"
      onClose={onClose}
      onClick={(e) => {
        // A click on the backdrop (outside the content) closes the dialog.
        if (e.target === dialogRef.current) onClose();
      }}
      data-testid="profile-dialog"
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 id="profile-title">{t('profile.title')}</h2>
          <button type="button" className={styles.close} onClick={onClose} aria-label={t('profile.close')}>
            ×
          </button>
        </div>
        <p className={styles.name}>{me.displayName}</p>
        <dl className={styles.facts}>
          <dt>{t('profile.email')}</dt>
          <dd>{me.email}</dd>
          {membership && (
            <>
              <dt>{t('profile.choir')}</dt>
              <dd>{membership.choirName}</dd>
              {membership.defaultPart && (
                <>
                  <dt>{t('profile.part')}</dt>
                  <dd>{t(`parts.${membership.defaultPart}`)}</dd>
                </>
              )}
              <dt>{t('profile.roles')}</dt>
              <dd>{membership.roles.map((r) => t(`roles.${r}`)).join(', ')}</dd>
            </>
          )}
        </dl>

        <div className={styles.language}>
          <span className={ui.label} id="language-label">
            {t('profile.language')}
          </span>
          <div className={styles.segmented} role="radiogroup" aria-labelledby="language-label">
            {LANGUAGES.map((language) => (
              <button
                key={language}
                type="button"
                role="radio"
                lang={language}
                aria-checked={i18n.language === language}
                disabled={readOnly}
                className={`${styles.segment} ${i18n.language === language ? styles.on : ''}`}
                onClick={() => choose(language)}
              >
                {t(`languages.${language}`)}
              </button>
            ))}
          </div>
          <p className={ui.muted}>{t('profile.languageHint')}</p>
          {updateLanguage.isError && (
            <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
              {t('profile.saveFailed')}
            </div>
          )}
        </div>

        <p className={styles.privacy}>
          <button type="button" className={ui.linkButton} onClick={() => setPrivacyOpen(true)}>
            {t('privacy.open')}
          </button>
          {me.privacyNoticeReadAt && <span className={ui.muted}> · {t('privacy.readOn', { date: formatDate(me.privacyNoticeReadAt, dateLocale()) })}</span>}
        </p>

        <p className={styles.version} data-testid="app-version">
          {t('profile.version', { version: APP_VERSION })}
        </p>

        <div className={styles.actions}>
          <button type="button" className={ui.button} onClick={onClose}>
            {t('profile.close')}
          </button>
        </div>
      </div>
      <Dialog open={privacyOpen} title={t('privacy.open')} onClose={() => setPrivacyOpen(false)} wide data-testid="privacy-dialog">
        <PrivacyNotice />
      </Dialog>
    </dialog>
  );
}
